import {
  appState,
  setCustomer,
  showError,
  startLoading,
  stopLoading,
  useAppDispatch,
  useAppSelector,
} from "app";
import { useEffect, useState } from "react";
import { useLoadEgerdauToken } from "./useLoadEgerdauToken";
import { useLoadEgerdauCustomer } from "./useLoadEgerdauCustomer";
import { useLoadSAPData } from "./useLoadSAPData";
import { DD_LOGS, DD_RUM, datadogDisabled } from "app/DataDogRUM";
import { datadogRum } from "@datadog/browser-rum";
import { AuthMe, EgerdauCustomer, getEnv, toggleQqtUserProfileEndpoint } from "common/services";
import { trackCustomDimensions } from "common/tagManagerService";
import { datadogLogs } from "@datadog/browser-logs";

interface UseAppInitArgs {
  /** Whether the backend currently offers Okta login (from GET /auth/providers). */
  oktaEnabled: boolean;
  /**
   * Whether the backend currently offers the legacy eGerdau path (from GET /auth/providers).
   * Only affects the wording of the boot loader — the bootstrap itself runs whenever Okta is not
   * the active provider, including when `/auth/providers` could not be reached and every provider
   * reads as disabled.
   */
  egerdauEnabled: boolean;
  /** True while the provider toggles are still loading. */
  providersLoading: boolean;
  /** True when a QQT BFF cookie session is active (resolved via /auth/me). */
  isAuthenticated: boolean;
  /** The current principal from /auth/me when an Okta/BFF session exists. */
  me?: AuthMe;
}

/**
 * Maps the minimal BFF principal (`/auth/me`) onto the legacy {@link EgerdauCustomer} shape the
 * rest of the app reads from the `app` slice. The BFF only returns identity claims (no soldTo /
 * SAP org data), so downstream code falls back to its default soldTo until the user selects one.
 */
const mapAuthMeToCustomer = (me: AuthMe): EgerdauCustomer => {
  const parts = (me.displayName || "").trim().split(/\s+/);
  const [firstName, ...rest] = parts;
  return {
    uid: me.email || me.subject,
    displayUid: me.email,
    name: me.displayName || me.email,
    firstName: firstName || undefined,
    lastName: rest.length ? rest.join(" ") : undefined,
  };
};

export const useAppInit = ({
  oktaEnabled,
  egerdauEnabled,
  providersLoading,
  isAuthenticated,
  me,
}: UseAppInitArgs) => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState<boolean>(false);
  const [appInitComplete, setAppInitComplete] = useState<boolean>(false);
  const [datadogInitComplete, setDDInitComplete] = useState<boolean>(false);
  const hostname = window?.location.hostname;

  // When Okta is the active provider, browser auth is driven by the BFF cookie session
  // (/auth/me) and the Okta login page — the legacy eGerdau profile bootstrap must NOT run.
  // Otherwise its boot overlay covers the Okta login surface and never clears (there is no
  // eGerdau token/customer/SAP data to complete it). While providers are still resolving we also
  // hold off so we don't kick eGerdau prematurely.
  const egerdauInitEnabled = !providersLoading && !oktaEnabled;

  // An active Okta/BFF cookie session — its identity comes from /auth/me, not an eGerdau token.
  const oktaAuthenticated = oktaEnabled && isAuthenticated && Boolean(me);

  // Datadog initialization
  useEffect(() => {
    if (!datadogInitComplete && !error) {
      DD_RUM();
      DD_LOGS();
      setDDInitComplete(true);
    }
  }, [datadogInitComplete, error]);
  // eGerdau initialization data
  const { customer, accessToken, sapAuxProfile } = useAppSelector(appState);
  const {
    error: tokenError,
    isRedirect,
    remoteTokenError,
    authCode,
    cachedAccessToken,
  } = useLoadEgerdauToken();

  datadogLogs.logger.info(
    "[eGerdau Login] " +
      "hostname: " +
      hostname +
      ", URL:" +
      window.location.href +
      ", isRedirect: " +
      isRedirect +
      ", authCode: " +
      authCode +
      ", cachedAccessToken present: " +
      !!cachedAccessToken +
      ", appInitComplete: " +
      appInitComplete,
  );
  if (remoteTokenError) {
    datadogLogs.logger.error("[eGerdau Remote Token Error] error - " + remoteTokenError);
  }

  const { error: customerError } = useLoadEgerdauCustomer(accessToken, oktaAuthenticated);
  const { error: sapError } = useLoadSAPData(customer, oktaAuthenticated);

  // Bootstrap an Okta/BFF session into the legacy customer shape the app reads from state, so
  // downstream screens (and appInitComplete) have a customer to work with. The BFF returns only
  // identity claims — soldTo / SAP org data is loaded separately (or defaulted) by useLoadSAPData.
  //
  // Deferred when `toggleQqtUserProfileEndpoint` is ON: there the profile (including the
  // server-authoritative soldTo / shipTo scope) comes from GET /users/current, and seeding the
  // claims-only shape first would set `customer` and permanently park that request on skipToken.
  // Once that lookup fails we seed anyway — see the fallback below.
  useEffect(() => {
    if (toggleQqtUserProfileEndpoint) return;
    if (oktaAuthenticated && me && !customer) {
      dispatch(setCustomer(mapAuthMeToCustomer(me)));
    }
  }, [oktaAuthenticated, me, customer, dispatch]);

  // Silent fallback for the QQT profile lookup. The backend answers 404 when MuleSoft has no QQT
  // profile for the session email, which is a normal outcome for a Gerdau user who is not
  // registered there — not a login failure. Rather than blocking the app, fall back to the Okta
  // identity claims: the user keeps their own email and name, and soldTo stays unset so
  // useLoadSAPData applies the same default it used before this toggle existed. Logged for
  // visibility, never surfaced to the user.
  useEffect(() => {
    if (!toggleQqtUserProfileEndpoint) return;
    if (!oktaAuthenticated || !me || customer || !customerError) return;
    datadogLogs.logger.info(
      "[QQT profile lookup] no profile for the session email — falling back to Okta identity claims",
      { error: customerError, email: me.email },
    );
    dispatch(setCustomer(mapAuthMeToCustomer(me)));
  }, [oktaAuthenticated, me, customer, customerError, dispatch]);

  // Drive the top-level boot loader based on the resolved auth provider.
  useEffect(() => {
    if (error) return;
    if (providersLoading) {
      // Still resolving which providers are enabled — show a neutral boot spinner.
      dispatch(startLoading({ message: "Loading" }));
    } else if (oktaEnabled) {
      if (isAuthenticated && !appInitComplete) {
        // Authenticated Okta session still hydrating its profile / SAP data — keep a spinner up
        // (rather than a blank page) until appInitComplete.
        dispatch(startLoading({ message: "Loading your profile" }));
      } else {
        // Not yet authenticated (the Okta login page renders its own UI) or init already done —
        // clear the boot loader so the legacy eGerdau overlay never covers the Okta login surface.
        dispatch(stopLoading());
      }
    } else if (!appInitComplete) {
      // The legacy bootstrap is running. Only name eGerdau when the backend actually reports it as
      // an enabled provider — this branch is also where we land when /auth/providers is
      // unreachable and every provider reads as disabled, and naming a provider that is off (or
      // unknown) misdirects anyone reading the screen or a support ticket.
      dispatch(
        startLoading({
          message: egerdauEnabled ? "Loading your eGerdau profile" : "Loading your user profile",
        }),
      );
    }
  }, [
    providersLoading,
    oktaEnabled,
    egerdauEnabled,
    isAuthenticated,
    appInitComplete,
    error,
    dispatch,
  ]);

  useEffect(() => {
    if (!egerdauInitEnabled) return;
    if (tokenError || customerError || sapError) {
      setError(true);
      const title = "Egerdau Login Failure";
      const message =
        "Unable to process your login at this time, Please refresh your browser or try again later. Token: missing " +
        tokenError +
        " Unable to lookup customer " +
        customerError +
        " SAP: " +
        sapError;
      datadogLogs.logger.error("[eGerdau Login Error]: Egerdau Login Failure", {
        error: message,
        remoteTokenError: remoteTokenError,
      });
      dispatch(
        showError({
          critical: true,
          title: title,
          message: message,
        }),
      );
    }
  }, [egerdauInitEnabled, tokenError, customerError, sapError, remoteTokenError, dispatch]);

  // An Okta/BFF session raises no blocking errors during init. Both remote dependencies degrade
  // instead: a failed profile lookup falls back to the /auth/me identity claims, and a failed SAP
  // auxiliary lookup falls back to a soldTo/shipTo-only profile (see useLoadSAPData's
  // `allowDegraded`). Both log to Datadog; neither shows the user a dialog, because a session that
  // authenticated successfully is not a login failure. The legacy eGerdau path above is unchanged.

  useEffect(() => {
    // Auth is satisfied by either the legacy eGerdau access token or an Okta/BFF cookie session.
    const authReady = Boolean(accessToken) || oktaAuthenticated;
    if (authReady && customer && sapAuxProfile) {
      dispatch(stopLoading());
      setAppInitComplete(true);
      const env = getEnv();
      if (!datadogDisabled && (env === "PROD" || env === "DEV" || env === "QA")) {
        datadogRum.startSessionReplayRecording();
      }
    }
  }, [customer, accessToken, sapAuxProfile, oktaAuthenticated, dispatch]);
  useEffect(() => {
    trackCustomDimensions(customer);
  }, [customer]);
  return {
    isRedirect,
    appInitComplete,
  };
};
